import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { linkService } from '../services/linkService';

export default function Redirect() {
  const { slug } = useParams<{ slug: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [link, setLink] = useState<any>(null);

  useEffect(() => {
    async function redirectToUrl() {
      if (!slug) {
        setError('No slug provided');
        setLoading(false);
        return;
      }

                   try {
               setLoading(true);
               const linkData = await linkService.getLinkBySlug(slug);
               
               if (!linkData) {
                 setError('Link not found');
                 setLoading(false);
                 return;
               }

               // Track the hit - detect if it's from QR code
               try {
                 console.log('Tracking hit for slug:', slug);
                 const isQrCode = window.location.search.includes('utm_source=qr-code');
                 await linkService.trackHit(slug, isQrCode);
                 console.log('Hit tracked successfully, isQrCode:', isQrCode);
               } catch (trackError) {
                 console.warn('Failed to track hit:', trackError);
                 // Don't fail the redirect if tracking fails
               }

               setLink(linkData);
               
               // Add a small delay to ensure the page loads properly
               setTimeout(() => {
          try {
            // Try multiple redirect methods for better mobile compatibility
            if (window.location.href !== linkData.destinationUrl) {
              // Method 1: Direct location change
              window.location.href = linkData.destinationUrl;
            }
          } catch (redirectError) {
            console.error('Redirect failed:', redirectError);
            // Fallback: show manual redirect option
            setError('Automatic redirect failed. Please click the button below.');
            setLoading(false);
          }
        }, 100);

      } catch (error: any) {
        console.error('Link retrieval error:', error);
        
        // Check if it's a permission error
        if (error.message && error.message.includes('permission')) {
          setError('Access denied. This may be due to mobile browser restrictions. Please try scanning again or copy the URL manually.');
        } else if (error.message && error.message.includes('network')) {
          setError('Network error. Please check your internet connection and try again.');
        } else {
          setError('Failed to redirect: ' + error.message);
        }
        
        setLoading(false);
      }
    }

    redirectToUrl();
  }, [slug, retryCount]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setError('');
    setLoading(true);
  };

  const handleManualRedirect = () => {
    if (link?.destinationUrl) {
      // Try to open in new tab/window
      try {
        window.open(link.destinationUrl, '_blank');
      } catch (e) {
        // Fallback to current window
        window.location.href = link.destinationUrl;
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 rounded-full animate-spin border-t-gray-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Redirecting...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 flex items-center justify-center py-12 px-4">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-amber-200/30 to-orange-300/30 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-red-200/30 to-orange-300/30 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-2xl mx-auto text-center">
          {/* Error Icon */}
          <div className="mb-8">
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Redirect Failed</h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">{error}</p>
          </div>
          
          {/* Link Information */}
          {link?.destinationUrl && (
            <div className="mb-8 p-6 bg-white/80 backdrop-blur-sm rounded-3xl border border-white/20 shadow-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Destination URL</h3>
              <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
                <p className="text-sm text-gray-600 break-all font-mono">
                  {link.destinationUrl}
                </p>
              </div>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="space-y-4 mb-8">
            {link?.destinationUrl && (
              <button
                onClick={handleManualRedirect}
                className="w-full max-w-md bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 flex items-center justify-center space-x-3 mx-auto"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                <span>Click to Continue</span>
              </button>
            )}
            
            <button
              onClick={handleRetry}
              className="w-full max-w-md bg-gray-600 hover:bg-gray-700 text-white px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center justify-center space-x-3 mx-auto"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Try Again</span>
            </button>
          </div>
          
          {/* Help Section */}
          <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-8 border border-white/20 shadow-xl">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Having trouble?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2h-2m-6-4l2 2m0 0l2-2m-2 2V4" />
                  </svg>
                </div>
                <h4 className="font-medium text-gray-900 mb-2">Copy & Paste</h4>
                <p className="text-sm text-gray-600">Copy the URL above and paste it manually in your browser</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h4 className="font-medium text-gray-900 mb-2">Try Different App</h4>
                <p className="text-sm text-gray-600">Use a different QR scanner app or your phone's camera</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                </div>
                <h4 className="font-medium text-gray-900 mb-2">Check Connection</h4>
                <p className="text-sm text-gray-600">Ensure you have a stable internet connection</p>
              </div>
            </div>
          </div>
          
          {/* Back to Home */}
          <div className="mt-8">
            <a 
              href="/"
              className="inline-flex items-center space-x-2 text-amber-600 hover:text-amber-700 font-medium transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Back to home</span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

